import { Track } from "@/src/lib/tracking";
import { ospThemes } from "@ospweb/sdk-ui";
import { transformIpfs } from "utils";
import {
  createOrSelectGroup,
  drawGridLines,
  drawLine,
  drawLineShadow,
  drawTickLabel,
  generateTicks,
  getMaxTextWidth,
  getSVG,
  getScale,
  getSeriesGroups,
  handleChartResize,
} from "./utils";
import type {
  HTMLEl,
  IAddInteractionsParams,
  IAddLineIndicatorsParams,
  ICalculateTooltipPositionParams,
  ICleanup,
  IDrawEndCallback,
  IGenerateGetClosestX,
  IGetClosestX,
  IGetSizeResult,
  IParams,
  IPreprocessDataResult,
  ISegmentData,
  IShowTooltip,
  IToggleLine,
  ITooltipData,
  MaybeNull,
  SVGLine,
} from "./type";

// 常量配置
const AXIS_TEXT_SIZE = 10; // 坐标轴文本大小
const GRID_COLOR = "var(--edgen-border_white_transparent10)"; // 网格线颜色
const AXIS_TEXT_COLOR = "var(--edgen-border_white_transparent20)"; // 坐标轴文本颜色
const POINT_SIZE = 3; // 交互点的大小
const CROSSHAIR_COLOR = "var(--edgen-content_tertiary)"; // 交叉线的颜色
const CROSSHAIR_LABEL_COLOR = "var(--edgen-content_secondary)"; // 交叉线标签颜色
const CROSSHAIR_LABEL_FONT_SIZE = "10px"; // 交叉线标签字体大小
const CROSSHAIR_LABEL_BG = ospThemes.colors.background_solid_primary; // 交叉线标签背景

// 图表布局配置
const CHART_MARGIN = {
  top: 20,
  right: 6,
  bottom: 15,
  left: 0,
};

export const createDualYAxisLineChart = (
  options: IParams,
  drawEndCallback?: IDrawEndCallback,
): MaybeNull<{
  cleanup: ICleanup;
  toggleLine: IToggleLine;
}> => {
  const { XAxis, YAxis, config, ticks, others } = options;
  // 获取tooltipFormat函数和自定义tooltip格式化函数（从config中）
  const { tooltipValueFormat, tooltipFormatter } = config;

  // 验证必要参数
  if (!XAxis || XAxis.length === 0) {
    Track.log(
      "error",
      "createDualYAxisLineChart",
      "X-axis data cannot be empty",
    );
    return null; // 返回空函数作为清理函数
  }

  const container = config.el;
  if (!container) {
    Track.log(
      "error",
      "createDualYAxisLineChart",
      "Container element not provided",
    );
    return null; // 返回空函数作为清理函数
  }
  if (!window.d3?.select) {
    Track.log(
      "warn",
      "createDualYAxisLineChart",
      "d3 is unavailable, skip drawing",
    );
    return null;
  }
  const margin = { ...CHART_MARGIN, ...config.margin };
  // YAxis.left = baseData;

  /**
   * 校验Y轴数据方法
   * @returns 数据是否有效
   * @throws 如果数据无效，抛出错误
   */
  const checkYAxisData = () => {
    if (!YAxis?.left || !(YAxis?.left?.length > 0)) {
      throw new Error("Left axis data cannot be empty");
    }
    return true;
  };

  // 尝试校验数据，如果失败则提前返回
  try {
    checkYAxisData();
  } catch (error) {
    Track.log(
      "error",
      "createDualYAxisLineChart",
      `Data validation failed: ${(error as Error).message}`,
    );
    return null; // 返回空函数作为清理函数
  }

  let _preprocessDataResult: IPreprocessDataResult;

  /**
   * 获取图表的各种宽高尺寸
   * @returns 包含图表尺寸信息的对象
   */
  const getSizes = (): IGetSizeResult => {
    try {
      const containerWidth = container.clientWidth;
      const innerWidth = containerWidth - margin.left - margin.right;
      const containerHeight = container.clientHeight;
      const innerHeight = containerHeight - margin.top - margin.bottom;
      return {
        containerWidth,
        innerWidth,
        containerHeight,
        innerHeight,
      };
    } catch (error) {
      Track.log("error", "createDualYAxisLineChart", "Error getting sizes:", {
        cause: String(error),
      });
      return {
        containerWidth: 0,
        innerWidth: 0,
        containerHeight: 0,
        innerHeight: 0,
      };
    }
  };
  /**
   * 数据预处理
   * @returns {IPreprocessDataResult}
   */
  const preprocessData = (): IPreprocessDataResult => {
    const yValues: number[] = [];
    // 关键刻度的在X轴中的索引信息
    const tickList: IPreprocessDataResult["tickList"] = [];
    // 每一条折线的分段数据
    const segments: IPreprocessDataResult["segments"] = {};
    // 每一条折线拉平后的数据
    const lineFlatData: IPreprocessDataResult["lineFlatData"] = {};
    // 每一条折线在X轴有数据的点的颜色值
    const lineColorMap: IPreprocessDataResult["lineColorMap"] = {};
    YAxis.left.forEach((line) => {
      const segmentData: Array<ISegmentData | null> = [];
      let lineIndex = 0;
      lineFlatData[line.name] = [];
      lineColorMap[line.name] = {};
      // 每一条折线的数据
      line.data.forEach((d) => {
        if (typeof d === "number") {
          yValues.push(d);
          const latest = segmentData.at(-1); // 获取最新的段
          if (latest) {
            latest.data.push({ x: lineIndex, y: d });
          } else {
            segmentData.push({
              color: line.color,
              lineWidth: line.lineWidth,
              shadow: line.shadow,
              data: [{ x: lineIndex, y: d }],
            });
          }
          lineFlatData[line.name].push(d);
          lineColorMap[line.name][lineIndex] = line.color;
          lineIndex++;
        } else if (d?.data) {
          segmentData.push(null);
          // 如果折线进行了分段
          d.data.forEach((item) => {
            lineFlatData[line.name].push(item);
            if (typeof item === "number") {
              const latest = segmentData.at(-1); // 获取累加器中最新的段
              if (latest) {
                latest.data.push({ x: lineIndex, y: item });
              } else {
                segmentData.push({
                  color: d.color || line.color,
                  lineWidth: d.lineWidth || line.lineWidth,
                  shadow: d.shadow || line.shadow,
                  data: [{ x: lineIndex, y: item }],
                });
              }
              yValues.push(item);
              lineColorMap[line.name][lineIndex] = d.color || line.color;
              lineIndex++;
            } else {
              // 如果折线数据是null
              segmentData.push(null);
              lineColorMap[line.name][lineIndex] = null;
              lineIndex++;
            }
          });
          segmentData.push(null);
        } else {
          // 如果折线数据是null
          lineFlatData[line.name].push(null);
          segmentData.push(null);
          lineColorMap[line.name][lineIndex] = null;
          lineIndex++;
        }
      });
      const result = segmentData.filter((d) => d !== null);
      // 为了避免线断掉，如果一段的结束和下一段的开始是之间X相隔1，就将后一段的最后一个元素，添加到前一段的结尾
      if (result.length > 1) {
        for (let i = 0; i < result.length - 1; i++) {
          const prev = result[i];
          const next = result[i + 1];
          if (prev.data[prev.data.length - 1].x + 1 === next.data[0].x) {
            prev.data.push(next.data[0]);
          }
        }
      }

      segments[line.name] = {
        name: line.name,
        data: result,
      };
    });
    let xIndex = 0;
    ticks?.xKeyTicks?.forEach((d) => {
      while (xIndex < XAxis.length) {
        if (XAxis[xIndex].x === d.x) {
          tickList.push({ ...d, index: xIndex });
          xIndex++;
          break;
        }
        xIndex++;
      }
    });
    _preprocessDataResult = {
      yValues,
      tickList,
      segments,
      lineFlatData,
      lineColorMap,
    };
    return _preprocessDataResult;
  };

  // 应用标签样式
  const applyLabelStyles = (element: HTMLEl) => {
    element
      .style("color", CROSSHAIR_LABEL_COLOR)
      .style("font-size", CROSSHAIR_LABEL_FONT_SIZE)
      .style("background-color", CROSSHAIR_LABEL_BG)
      .style("padding", "2px 6px")
      .style("border-radius", "4px")
      .style("line-height", "12px")
      .style("position", "absolute")
      .style("font-weight", "600")
      .style("white-space", "nowrap")
      .style("backdrop-filter", "blur(2px)");
  };
  // 应用参考线样式
  const applyCrosshairLineStyles = (element: SVGLine) => {
    element.style("stroke", CROSSHAIR_COLOR).style("stroke-width", 1);
    element.style("stroke-dasharray", "3,3");
  };

  // 闭包 用来生成获取最近X轴数据点的函数
  const generateGetClosestX: IGenerateGetClosestX = (params) => {
    const { svg, xScale, leftYScale } = params;
    const { xValues, lineFlatData, lineColorMap } = params;

    // 鼠标hover到图上的时候，根据event事件获取距离鼠标位置最近的X轴数据点
    return (sourceEvent) => {
      const event =
        sourceEvent instanceof TouchEvent
          ? sourceEvent.changedTouches[0]
          : sourceEvent;
      const [mouseX, mouseY] = window.d3.pointer(event, svg.node());

      // 原有全图交互逻辑（适用于所有模式）
      const xValue = xScale.invert(mouseX);
      if (xValue < xValues[0] || xValue > xValues[xValues.length - 1]) {
        // 超出X轴数据范围
        return {
          x: null,
          yMap: {},
          xIndex: null,
          mouseX,
          mouseY,
          colorMap: {},
          milestone: undefined,
        };
      }
      const bisect = window.d3.bisector((d) => d).center;
      const finalXIndex = bisect(xValues, xValue);
      // 最终的X坐标索引对应的X轴的坐标
      const finalX = xScale(xValues[finalXIndex]);
      const distance = Math.abs(mouseX - finalX);
      // 与最近的点距离过远，超过10
      if (distance > 10) {
        return {
          x: null,
          yMap: {},
          xIndex: null,
          mouseX,
          mouseY,
          colorMap: {},
          milestone: undefined,
        };
      }
      const y: Record<string, number> = {};
      const colorMap: Record<string, string> = {};
      Object.keys(lineFlatData).forEach((key) => {
        const value = lineFlatData[key][finalXIndex];
        if (typeof value === "number") {
          y[key] = leftYScale(value);
        }
        const color = lineColorMap[key][finalXIndex];
        if (color) {
          colorMap[key] = color;
        }
      });

      // 检查当前点是否是里程碑点
      const matchedMilestone = options.milestones?.find(
        (m) => m.x === finalXIndex,
      );

      return {
        x: finalX,
        yMap: y,
        colorMap,
        xIndex: finalXIndex,
        mouseX,
        mouseY,
        milestone: matchedMilestone,
      };
    };
  };

  // 闭包 生成tooltip
  const generateTooltip = (): IShowTooltip => {
    const el = window.d3.select(config.el);

    // 基础容器：只有定位相关样式
    const tooltip = el
      .selectAll(".tooltip")
      .data([null])
      .join("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("top", "0")
      .style("left", "0")
      .style("z-index", "1")
      .style("pointer-events", "none")
      .style("display", "none");

    // 如果没有自定义格式化函数，添加默认样式和结构
    if (!tooltipFormatter) {
      tooltip
        .attr(
          "class",
          "tooltip p-3 rounded-lg bg-background_solid_secondary flex flex-col gap-3",
        )
        .style(
          "border",
          `1px solid ${ospThemes.colors.border_white_transparent10}`,
        )
        .style("width", "clamp(100px, 200px, 400px)");

      const timeEl = tooltip
        .selectAll(".time")
        .data([null])
        .join("span")
        .attr("class", "time body_medium_xs text-content_tertiary");

      tooltip
        .selectAll<HTMLDivElement, (typeof YAxis.left)[0]>(".row")
        .data(YAxis.left, (d) => d.name)
        .join((enter) => {
          const row = enter
            .append("div")
            .attr(
              "class",
              (d) =>
                `row row-${d.name} flex items-center justify-between body_medium_s`,
            );

          const left = row
            .append("div")
            .attr("class", "left flex items-center");

          if (YAxis.left.length > 1) {
            left
              .append("span")
              .attr("class", "w-1 h-1 rounded-full flex mr-1")
              .style("background-color", (d) => d.color);
          }

          left
            .filter((d) => !!d.logo)
            .append("img")
            .attr("class", "w-3 h-3 rounded-full flex mr-0.5")
            .attr("src", (d) => transformIpfs(d.logo!));

          left
            .append("span")
            .attr("class", "text-content_tertiary")
            .text((d) => d.name);

          row.append("div").attr("class", "right");

          return row;
        });

      // 返回默认渲染函数
      return (time, data, _tooltipData) => {
        timeEl.text(time);
        tooltip
          .selectAll<HTMLDivElement, (typeof YAxis.left)[0]>(".row")
          .style("display", (d) => (data[d.name] ? "flex" : "none"))
          .each(function (d) {
            const value = data[d.name];
            if (!value) {
              return;
            }

            window.d3
              .select(this)
              .select<HTMLDivElement>(".right")
              .html(value.label)
              .style(
                "color",
                value.trend === "rise"
                  ? "var(--edgen-content_accent1_primary)"
                  : value.trend === "fall"
                    ? "var(--edgen-content_accent2_primary)"
                    : "var(--edgen-content_primary)",
              );
          });
        return tooltip;
      };
    }

    // 有自定义格式化函数：返回自定义渲染函数
    return (time, _data, tooltipData) => {
      if (tooltipData) {
        const htmlContent = tooltipFormatter(tooltipData.xIndex);
        tooltip.html(htmlContent);
        return tooltip;
      }
      // 降级处理
      tooltip.html(`<div>${time}</div>`);
      return tooltip;
    };
  };
  // 计算tooltip位置
  const calculateTooltipPosition = (
    params: ICalculateTooltipPositionParams,
  ) => {
    const { svgSize, tooltipSize } = params;
    const {
      mousePosition: { x: mouseX, y: mouseY },
    } = params;
    const TOOLTIP_OFFSET = 10; // 关键点到 tooltip 的距离

    // 垂直位置：优先放在鼠标点上方，放不下再放在下方，始终避免鼠标位于 tooltip 正中间
    let tooltipY = mouseY - tooltipSize.height - TOOLTIP_OFFSET;
    if (tooltipY < 0) {
      // 上方空间不足时，放到下方
      tooltipY = mouseY + TOOLTIP_OFFSET;
    }
    // 垂直边界收缩在可见区域内
    const maxY = svgSize.height - tooltipSize.height;
    tooltipY = Math.max(0, Math.min(tooltipY, maxY));

    // 水平位置：与之前一致，判断鼠标在图的左半还是右半
    const halfWidth = svgSize.width / 2;
    let tooltipX;
    if (mouseX < halfWidth) {
      tooltipX = mouseX + TOOLTIP_OFFSET;
    } else {
      tooltipX = mouseX - tooltipSize.width - TOOLTIP_OFFSET;
    }

    return { tooltipX, tooltipY };
  };

  // 给每条折线绘制指示点
  const addLineIndicators = (params: IAddLineIndicatorsParams) => {
    const { svg, name: sourceName } = params;
    const name = sourceName.replace(/\s/g, "_");
    const pointGroup = svg
      .selectAll(`.point-group.point-${window.CSS.escape(name)}`)
      .data([null])
      .join("g")
      .attr("class", `point-group point-${name}`); // 【指示点】容器

    const point = pointGroup
      .selectAll(".point")
      .data([null])
      .join((enter) =>
        enter
          .append("circle")
          .attr("class", "point")
          .attr("r", POINT_SIZE)
          .style("opacity", 0),
      );
    return point;
  };

  // 添加交互
  const addInteractions = (params: IAddInteractionsParams) => {
    const { svg, leftMargin, rightMargin, topMargin, bottomMargin } = params;
    const { getClosestX, showTooltip, lineFlatData } = params;
    const el = window.d3.select(config.el).style("position", "relative");

    // Tooltip交互状态管理
    let isTooltipLocked = false;
    let hideTooltipTimeout: ReturnType<typeof setTimeout> | null = null;
    let currentActiveMilestoneX: number | null = null; // 记录当前激活的里程碑

    // 最近一次 hover 的 X 索引用于 click 透传
    let lastXIndex: number | null = null;

    // 创建十字线和标签和指示点
    const crosshair = svg
      .selectAll(".crosshair")
      .data([null])
      .join("g")
      .attr("class", "crosshair"); // 【十字线】容器

    const lineX = crosshair
      .selectAll(".crosshair-x")
      .data([null])
      .join("line")
      .attr("class", "crosshair-x"); // 【十字线】X轴

    const lineY = crosshair
      .selectAll(".crosshair-y")
      .data([null])
      .join("line")
      .attr("class", "crosshair-y"); // 【十字线】Y轴

    const crosshairLabels = el
      .selectAll(".crosshair-labels")
      .data([null])
      .join("div")
      .attr("class", "crosshair-labels"); // 【标签】容器

    const labelX = crosshairLabels
      .selectAll(".label-x")
      .data([null])
      .join("span")
      .attr("class", "label-x"); // 【标签】X轴

    const labelY = crosshairLabels
      .selectAll(".label-y")
      .data([null])
      .join("span")
      .attr("class", "label-y"); // 【标签】Y轴
    const pointGroup = svg.selectAll(".point-group"); // 【指示点】容器
    // 添加初始样式
    crosshair.style("opacity", 0);
    crosshairLabels
      .style("position", "absolute")
      .style("pointer-events", "none");
    crosshairLabels.style("left", 0).style("top", 0).style("display", "none");
    lineX.attr("x1", leftMargin).attr("x2", rightMargin);
    lineY.attr("y1", topMargin).attr("y2", bottomMargin);
    applyCrosshairLineStyles(lineX);
    applyCrosshairLineStyles(lineY);
    applyLabelStyles(labelX);
    applyLabelStyles(labelY);
    // 计算SVG的宽高
    const svgSize = svg.node()!.getBoundingClientRect();

    // 清除隐藏定时器
    function clearHideTimeout() {
      if (hideTooltipTimeout) {
        clearTimeout(hideTooltipTimeout);
        hideTooltipTimeout = null;
      }
    }

    // 隐藏十字线和标签
    function hideCrosshair(immediate = false) {
      // 如果tooltip被锁定，不隐藏
      if (isTooltipLocked) {
        return;
      }

      // 清除之前的定时器
      clearHideTimeout();

      const doHide = () => {
        crosshair.style("opacity", 0);
        crosshairLabels.style("display", "none");
        pointGroup.style("opacity", 0);
        window.d3.select(config.el).select(".tooltip").style("display", "none");
        // 重置里程碑激活状态
        if (options.milestones && options.milestones.length > 0) {
          svg.selectAll(".milestone-bg").attr("opacity", 0);
          currentActiveMilestoneX = null;
        }
      };

      // 检查是否为里程碑模式
      const hasMilestones = options.milestones && options.milestones.length > 0;

      // 立即隐藏或延迟隐藏
      if (immediate) {
        doHide();
      } else if (hasMilestones) {
        // 里程碑模式：添加延迟，给用户时间将鼠标移动到 tooltip 上
        hideTooltipTimeout = setTimeout(doHide, 150);
      } else {
        // 普通模式：立即隐藏
        doHide();
      }
    }
    // 显示十字线和标签
    function showCrosshair(
      params: Omit<Required<ReturnType<IGetClosestX>>, "milestone"> & {
        milestone?: ReturnType<IGetClosestX>["milestone"];
      },
    ) {
      const { x, yMap, xIndex, mouseX, mouseY, colorMap } = params;
      if (typeof x !== "number") {
        return;
      }

      // 显示 tooltip 时清除隐藏定时器
      clearHideTimeout();
      // 重新计算容器和SVG的偏移量（确保使用最新位置）
      const containerRect = (config.el as HTMLElement).getBoundingClientRect();
      const svgRect = svg.node()!.getBoundingClientRect();
      const svgOffsetX = svgRect.left - containerRect.left;
      const svgOffsetY = svgRect.top - containerRect.top;
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      const mainY = yMap![YAxis.left[0].name];
      lineX.attr("y1", mainY).attr("y2", mainY).style("opacity", 1);
      labelX.style("transform", `translateY(-50%)`).style("opacity", 1);
      const rawValue = lineFlatData[YAxis.left[0].name][xIndex!];
      let formattedText = "";
      if (tooltipValueFormat) {
        formattedText = tooltipValueFormat(rawValue);
      } else if (typeof rawValue === "number") {
        formattedText = `$${rawValue}`;
      }
      labelX.style("top", `${mainY}px`).html(formattedText);
      labelX.style("left", `${margin.left}px`);
      if (!mainY) {
        labelX.style("opacity", 0);
        lineX.style("opacity", 0);
      }
      lineY.attr("x1", x).attr("x2", x);
      crosshair.style("opacity", 1);
      crosshairLabels.style("display", "block");
      const labelWidth = (labelY.node() as HTMLElement).offsetWidth;
      // 实时获取 SVG 宽度，避免闭包陷阱
      const svgWidth = svg.node()!.getBoundingClientRect().width;
      let newLeft = x;
      let newTransform = "translateX(-50%)";

      if (x - labelWidth / 2 < 0) {
        // 贴近左边缘
        newLeft = 0;
        newTransform = "translateX(0)";
      } else if (x + labelWidth / 2 > svgWidth) {
        // 贴近右边缘
        newLeft = svgWidth;
        newTransform = "translateX(-100%)";
      }
      labelY.style("left", `${newLeft}px`).style("transform", newTransform);
      labelY.style("top", `${bottomMargin}px`).text(XAxis[xIndex!].label);
      pointGroup.style("opacity", 1);

      // 判断是否显示tooltip（区分里程碑模式和普通模式）
      const hasMilestones = options.milestones && options.milestones.length > 0;

      if (hasMilestones) {
        // 【里程碑模式】：只在里程碑点显示tooltip
        if (!params.milestone) {
          // 非里程碑点：隐藏tooltip，但保持十字线显示
          window.d3
            .select(config.el)
            .select(".tooltip")
            .style("display", "none");
          // 重置所有里程碑的激活状态（仅在有激活的里程碑时）
          if (currentActiveMilestoneX !== null) {
            svg.selectAll(".milestone-bg").attr("opacity", 0);
            currentActiveMilestoneX = null;
          }
          return;
        }
        // 激活当前里程碑点（仅在里程碑切换时）
        if (currentActiveMilestoneX !== params.milestone.x) {
          svg.selectAll(".milestone-bg").attr("opacity", 0);
          svg
            .select(`.milestone-${params.milestone.x} .milestone-bg`)
            .transition()
            .duration(200)
            .attr("opacity", 1);
          currentActiveMilestoneX = params.milestone.x;
        }
      }

      // 构建数组结构的 seriesData 和用于默认渲染的 tooltipData
      const seriesData: ITooltipData["seriesData"] = [];
      const tooltipData: Record<
        string,
        { trend: "rise" | "fall" | "normal"; label: string }
      > = {};

      // 按照 YAxis.left 的顺序遍历，保持一致
      YAxis.left.forEach((series) => {
        const name = series.name;
        const point = svg
          .select(`.point-${window.CSS.escape(name.replace(/\s/g, "_"))}`)
          .select(".point");
        const y = yMap![name];
        const color = colorMap[name];
        const value = lineFlatData[name][xIndex!];

        // 更新指示点
        if (typeof y === "number") {
          point.attr("cy", y).attr("cx", x).style("opacity", 1);
          point.style("fill", color);
        } else {
          point.style("opacity", 0);
        }

        // 构建格式化的标签
        const label = tooltipValueFormat
          ? tooltipValueFormat(value)
          : typeof value === "number"
            ? `$${value}`
            : "";

        // 构建数组项（用于自定义格式化）
        seriesData.push({
          name,
          value,
          label,
          yPixel: typeof y === "number" ? y : null,
          color: color || "",
          rawDataArray: lineFlatData[name], // 传递完整的原始数据数组
        });

        // 构建默认渲染用的数据
        tooltipData[name] = {
          trend: "normal",
          label,
        };
      });

      // 构建完整的 tooltip 数据
      const tooltipFullData: ITooltipData = {
        time: XAxis[xIndex!].label,
        xIndex: xIndex!,
        seriesData,
        milestone: params.milestone, // 传递里程碑信息
      };

      // 记录 x 索引供点击事件使用
      lastXIndex = xIndex!;
      const tooltip = showTooltip(
        XAxis[xIndex!].label,
        tooltipData, // 用于默认渲染
        tooltipFullData, // 用于自定义格式化
      );
      if (Object.keys(tooltipData).length === 0) {
        tooltip.style("display", "none");
        return;
      }
      // 为了正确测量尺寸，先显示但隐藏可见性
      tooltip.style("display", "block").style("visibility", "hidden");
      // 计算 tooltip 的尺寸
      const tooltipRect = (
        tooltip.node()! as HTMLElement
      ).getBoundingClientRect();

      let tooltipX: number, tooltipY: number;
      const TOOLTIP_OFFSET = 12; // 关键点到tooltip的距离

      // 里程碑模式：使用上下智能定位
      if (hasMilestones && params.milestone) {
        const currentY = yMap![Object.keys(yMap!)[0]]; // 十字线Y坐标
        const tooltipHeight = tooltipRect.height;
        const chartCenter = (topMargin + bottomMargin) / 2; // 图表中心线位置

        // 判断应该显示在上方还是下方（基于图表中线）
        const showAbove = currentY > chartCenter;
        // 计算基于SVG坐标系的tooltip位置
        const tooltipYInSvg = showAbove
          ? currentY - tooltipHeight - TOOLTIP_OFFSET // 上方显示
          : currentY + TOOLTIP_OFFSET; // 下方显示

        // 水平居中对齐十字线（基于SVG坐标系）
        const tooltipXInSvg = x! + leftMargin - tooltipRect.width / 2;

        // 将SVG坐标系转换为容器坐标系
        tooltipX = tooltipXInSvg + svgOffsetX;
        tooltipY = tooltipYInSvg + svgOffsetY;

        // 边界检查（基于容器尺寸）
        if (tooltipX < TOOLTIP_OFFSET) {
          tooltipX = TOOLTIP_OFFSET;
        }
        if (tooltipX + tooltipRect.width > containerWidth - TOOLTIP_OFFSET) {
          tooltipX = containerWidth - tooltipRect.width - TOOLTIP_OFFSET;
        }
        if (tooltipY < TOOLTIP_OFFSET) {
          tooltipY = TOOLTIP_OFFSET;
        }
        if (tooltipY + tooltipHeight > containerHeight - TOOLTIP_OFFSET) {
          tooltipY = containerHeight - tooltipHeight - TOOLTIP_OFFSET;
        }
      } else {
        // 普通模式：使用原有定位逻辑
        const position = calculateTooltipPosition({
          svgSize,
          tooltipSize: tooltipRect,
          mousePosition: { x: mouseX, y: mouseY },
        });
        // 将SVG坐标系转换为容器坐标系
        tooltipX = position.tooltipX + svgOffsetX;
        tooltipY = position.tooltipY + svgOffsetY;

        // 边界检查（基于容器尺寸）
        // 注意：calculateTooltipPosition 内部已有 Y 轴检查（基于 svgSize），
        // 但转换后可能超出容器，且 X 轴检查不完整，所以需要重新检查
        if (tooltipX < TOOLTIP_OFFSET) {
          tooltipX = TOOLTIP_OFFSET;
        }
        if (tooltipX + tooltipRect.width > containerWidth - TOOLTIP_OFFSET) {
          tooltipX = containerWidth - tooltipRect.width - TOOLTIP_OFFSET;
        }
        // Y 轴：虽然 calculateTooltipPosition 已检查，但转换后可能超出容器
        if (tooltipY < TOOLTIP_OFFSET) {
          tooltipY = TOOLTIP_OFFSET;
        }
        if (tooltipY + tooltipRect.height > containerHeight - TOOLTIP_OFFSET) {
          tooltipY = containerHeight - tooltipRect.height - TOOLTIP_OFFSET;
        }
      }

      tooltip
        .style("display", "flex")
        .style("transform", `translate(${tooltipX}px, ${tooltipY}px)`)
        .style("visibility", "visible");
    }
    const handleHover = (event: MouseEvent) => {
      // 如果tooltip被锁定，不响应鼠标移动
      if (isTooltipLocked) {
        return;
      }

      const { x, yMap, xIndex, mouseX, mouseY, colorMap, milestone } =
        getClosestX(event);
      if (xIndex === null || !Object.keys(yMap).length) {
        return hideCrosshair();
      }

      showCrosshair({
        x: x!,
        yMap: yMap!,
        xIndex: xIndex!,
        mouseX,
        mouseY,
        colorMap,
        milestone,
      });
    };
    svg
      .style("cursor", "crosshair")
      .on("mouseover", handleHover)
      .on("mousemove", handleHover)
      .on("mouseout", () => hideCrosshair(false)) // 延迟隐藏
      .on("touchstart", handleHover)
      .on("touchmove", handleHover);

    // 启用 tooltip 交互（所有模式都支持）
    const tooltip = window.d3.select(config.el).select(".tooltip");

    // 修改 pointer-events 样式，允许交互
    tooltip.style("pointer-events", "auto");

    // 添加 tooltip 事件监听器
    tooltip
      .on("mouseenter", () => {
        // 鼠标进入 tooltip 时，清除隐藏定时器并锁定
        clearHideTimeout();
        isTooltipLocked = true;
      })
      .on("mouseleave", () => {
        // 鼠标离开 tooltip 后，解锁并立即隐藏
        isTooltipLocked = false;
        hideCrosshair(true); // 立即隐藏
      })
      .on("click", () => {
        // 仅当存在自定义 formatter 且外部提供点击回调时触发
        if (tooltipFormatter && typeof config.onTooltipClick === "function") {
          if (typeof lastXIndex === "number") {
            try {
              config.onTooltipClick(lastXIndex);
            } catch (err) {
              Track.log(
                "error",
                "createDualYAxisLineChart",
                "onTooltipClick error:",
                { cause: String(err) },
              );
            }
          }
        }
      });
  };

  // 切换某一条折线隐藏/显示
  const toggleLine: IToggleLine = (name) => {
    if (!name) {
      return;
    }
    const svg = window.d3.select(config.el).select("svg");
    const lineGroup = svg.select(`.line-${window.CSS.escape(name)}`);
    if (!lineGroup) {
      return;
    }
    const opacity = lineGroup.style("opacity");
    lineGroup.style("opacity", opacity === "0" ? "1" : "0");
  };

  /**
   * 绘制图表的主函数
   * 负责创建和更新图表的所有元素
   */
  const drawChart = () => {
    // 创建SVG
    const svg = getSVG(config.el, "dual-y-axis-line-chart");
    if (!svg) {
      return;
    }
    const { containerWidth, innerHeight } = getSizes();

    const _preprocessDataResult = preprocessData();
    const { yValues, tickList, segments, lineFlatData, lineColorMap } =
      _preprocessDataResult;
    const leftScale = getScale(
      [
        window.d3.min([...yValues, ...(others?.extraYAxisData || [])])!,
        window.d3.max([...yValues, ...(others?.extraYAxisData || [])])!,
      ],
      [innerHeight, margin.top],
    );
    // 获取Y轴刻度和比例尺
    const { ticks: leftTicks, tickLabelMap: leftFormattedTicksMap } =
      generateTicks({
        scale: leftScale,
        formatter: config.yAxisTickFormatter,
      });
    // 获取文本宽度和格式化的刻度值
    const leftMaxWidth = getMaxTextWidth(
      Object.values(leftFormattedTicksMap),
      svg,
    );

    // 创建轴组
    const leftAxisGroup = createOrSelectGroup(svg, "left-axis");
    // 折线组
    const lineGroup = createOrSelectGroup(svg, "line");

    // 添加网格线和轴标签
    const left = margin.left + leftMaxWidth + 10;
    const right = containerWidth - margin.right;
    drawGridLines({
      direction: "horizontal",
      start: left,
      end: right,
      ticks: leftTicks,
      scale: leftScale,
      svg: leftAxisGroup,
      color: GRID_COLOR,
      lineWidth: 1,
      lineType: "dashed",
    });

    drawTickLabel({
      svg: leftAxisGroup,
      scale: leftScale,
      direction: "vertical",
      fixedPosition: margin.left || leftMaxWidth,
      color: AXIS_TEXT_COLOR,
      fontSize: AXIS_TEXT_SIZE,
      ticks: leftTicks,
      tickLabelMap: leftFormattedTicksMap,
    });

    // 创建X轴组
    const xAxisGroup = createOrSelectGroup(svg, "x-axis");
    const xScale = getScale([0, XAxis.length - 1], [left, right]);
    const { xTicks, xTickLabelMap } = tickList.reduce<{
      xTicks: number[];
      xTickLabelMap: Record<number, string>;
    }>(
      (acc, curr) => {
        acc.xTicks.push(curr.index);
        acc.xTickLabelMap[curr.index] = curr.label;
        return acc;
      },
      { xTicks: [], xTickLabelMap: {} },
    );
    drawTickLabel({
      svg: xAxisGroup,
      scale: xScale,
      direction: "horizontal",
      fixedPosition: innerHeight + 15,
      color: AXIS_TEXT_COLOR,
      fontSize: AXIS_TEXT_SIZE,
      ticks: xTicks,
      tickLabelMap: xTickLabelMap,
    });

    const lines = Object.values(segments).map((d) => ({
      name: d.name,
      line: d.data.map((item) => ({
        data: item.data,
        color: item.color,
        lineWidth: item.lineWidth,
        shadow: item.shadow,
      })),
    }));
    const allGroups = getSeriesGroups(lineGroup, lines);

    allGroups.each(function (item) {
      const group = window.d3.select(this);

      addLineIndicators({
        svg: group as any,
        name: item.name,
      });

      drawLineShadow({
        svg: group,
        xScale,
        yScale: leftScale,
        line: item.line,
        name: item.name,
        color: item.color,
      });
      drawLine({
        svg: group,
        xScale,
        yScale: leftScale,
        line: item.line,
      });
    });

    // 渲染里程碑点（仅当配置了 milestones 时）
    if (options.milestones && options.milestones.length > 0) {
      const milestoneGroup = createOrSelectGroup(svg, "milestone-points");

      // 使用第一条折线（主折线）来定位 Y 坐标
      const mainLineName = YAxis.left[0].name;

      options.milestones.forEach((milestone) => {
        const xPos = xScale(milestone.x);
        const yValue = lineFlatData[mainLineName]?.[milestone.x];

        if (typeof yValue !== "number") {
          Track.log("warn", "里程碑", `x=${milestone.x} 位置无有效数据`);
          return;
        }

        const yPos = leftScale(yValue);

        // 为每个里程碑创建组，使用 milestone.x 作为标识
        const group = milestoneGroup
          .selectAll(`.milestone-${milestone.x}`)
          .data([milestone])
          .join("g")
          .attr("class", `milestone-group milestone-${milestone.x}`)
          .attr("transform", `translate(${xPos}, ${yPos})`)
          .style("cursor", "pointer");

        // 激活背景层：16px直径，仅在激活时显示
        group
          .selectAll(".milestone-bg")
          .data([milestone])
          .join("circle")
          .attr("class", "milestone-bg")
          .attr("r", 8)
          .attr("fill", milestone.focusColor)
          .attr("opacity", 0)
          .style("transition", "opacity 0.2s ease");

        // 中层圆：白色实心圆，10px直径
        group
          .selectAll(".milestone-middle")
          .data([milestone])
          .join("circle")
          .attr("class", "milestone-middle")
          .attr("r", 5)
          .attr("fill", "#fff");

        // 内层圆：彩色实心核心，6px直径
        group
          .selectAll(".milestone-inner")
          .data([milestone])
          .join("circle")
          .attr("class", "milestone-inner")
          .attr("r", 3)
          .attr("fill", milestone.color);
      });
    }

    // 渲染单点标注：折线上某点的标注点 + 向上虚线 + 文案（保证在画面内）
    const ann = options.annotation;
    const annGroup = createOrSelectGroup(svg, "chart-annotation");
    const mainLineNameForAnn = YAxis.left[0]?.name;
    if (
      ann &&
      mainLineNameForAnn &&
      typeof ann.x === "number" &&
      ann.x >= 0 &&
      ann.x < XAxis.length
    ) {
      const yValue = lineFlatData[mainLineNameForAnn]?.[ann.x];
      if (typeof yValue === "number") {
        const xPos = xScale(ann.x);
        const yPos = leftScale(yValue);
        // 标注点尺寸：圆心 5px，border 1.5px，外圈 2.5px
        const innerR = 2.5; // 圆心（不含 border）半径 = 5/2
        const borderR = 4; // 2.5 + 1.5
        const pointOuterR = 6.5; // 4 + 2.5
        const bubblePaddingX = 6;
        const bubblePaddingY = 2;
        const bubbleRadius = 4;
        const triangleWidth = 8;
        const triangleHeight = 6;
        const joinOverlap = 0.5;
        const tipGapFromPoint = 6;
        // 尖角仅做轻微钝化，避免“弯折感”
        const tipRadius = 2.8;
        const joinRadius = 1.2;
        const bubbleBgColor = ann.bubbleColor ?? ann.dashedLineColor;
        const labelTextColor = ann.labelColor ?? "#fff";
        const topBound = margin.top;
        const { innerHeight } = getSizes();
        const bottomBound = margin.top + innerHeight;

        const g = annGroup
          .selectAll(".annotation-group")
          .data([ann])
          .join("g")
          .attr("class", "annotation-group")
          // 标注仅做视觉展示，不参与交互，避免影响 hover tooltip 与十字线
          .style("pointer-events", "none")
          .attr("transform", `translate(${xPos}, ${yPos})`);

        // 外圈（border 外一圈，宽 2.5px）
        g.selectAll(".annotation-outer")
          .data([ann])
          .join("circle")
          .attr("class", "annotation-outer")
          .attr("r", pointOuterR)
          .attr("fill", ann.pointOuterRingColor);
        // 边框圈（宽 1.5px）
        g.selectAll(".annotation-border")
          .data([ann])
          .join("circle")
          .attr("class", "annotation-border")
          .attr("r", borderR)
          .attr("fill", ann.pointBorderColor);
        // 内圈（圆心宽高 5px）
        g.selectAll(".annotation-inner")
          .data([ann])
          .join("circle")
          .attr("class", "annotation-inner")
          .attr("r", innerR)
          .attr("fill", ann.pointInnerColor);

        const measureText = g
          .selectAll(".annotation-label-measure")
          .data([ann.label])
          .join("text")
          .attr("class", "annotation-label-measure")
          .attr("x", 0)
          .attr("y", 0)
          .attr("visibility", "hidden")
          .attr("font-size", "12px")
          .attr("font-weight", "700")
          .attr("font-family", "Satoshi, sans-serif")
          .text((d) => d);
        const measureNode = measureText.node() as SVGGraphicsElement | null;
        const textBBox = measureNode?.getBBox();
        const textWidth = Math.max(1, textBBox?.width ?? ann.label.length * 7);
        const textHeight = Math.max(1, textBBox?.height ?? 12);
        measureText.remove();

        const bubbleWidth = textWidth + bubblePaddingX * 2;
        const bubbleHeight = textHeight + bubblePaddingY * 2;
        const requiredVertical =
          pointOuterR + triangleHeight + bubbleHeight + 2;
        const canPlaceTop = yPos - topBound >= requiredVertical;
        const canPlaceBottom = bottomBound - yPos >= requiredVertical;
        const placeAbove = canPlaceTop || !canPlaceBottom;

        const bubbleCenterGlobalMin = left + bubbleWidth / 2;
        const bubbleCenterGlobalMax = right - bubbleWidth / 2;
        const bubbleCenterGlobal = Math.min(
          bubbleCenterGlobalMax,
          Math.max(bubbleCenterGlobalMin, xPos),
        );
        const bubbleCenterX = bubbleCenterGlobal - xPos;
        const bubbleLeft = bubbleCenterX - bubbleWidth / 2;
        const bubbleRight = bubbleCenterX + bubbleWidth / 2;
        const isFirstPoint = ann.x === 0;
        const isLastPoint = ann.x === XAxis.length - 1;
        const edgeCornerLeft = isFirstPoint;
        const edgeCornerRight = isLastPoint;
        // 三角底边必须落在气泡的直边区域（避开圆角），确保三角与气泡边完整连接
        const triangleCenterX = Math.min(
          bubbleRight - bubbleRadius - triangleWidth / 2,
          Math.max(bubbleLeft + bubbleRadius + triangleWidth / 2, 0),
        );

        const tipY = placeAbove
          ? -(pointOuterR + tipGapFromPoint)
          : pointOuterR + tipGapFromPoint;
        const triangleBaseYRaw = placeAbove
          ? tipY - triangleHeight
          : tipY + triangleHeight;
        // 轻微重叠，避免抗锯齿导致的细缝
        const triangleBaseY = placeAbove
          ? triangleBaseYRaw + joinOverlap
          : triangleBaseYRaw - joinOverlap;
        const bubbleTop = placeAbove
          ? triangleBaseY - bubbleHeight
          : triangleBaseY;
        const bubbleBottom = placeAbove
          ? triangleBaseY
          : triangleBaseY + bubbleHeight;
        const pointerHalf = triangleWidth / 2;
        const pointerLeft = triangleCenterX - pointerHalf;
        const pointerRight = triangleCenterX + pointerHalf;

        const buildBubblePath = () => {
          const left = bubbleLeft;
          const right = bubbleRight;
          const top = bubbleTop;
          const bottom = bubbleBottom;
          const r = Math.min(bubbleRadius, bubbleWidth / 2, bubbleHeight / 2);
          // 连接反向圆角在靠近左右角时自动收缩，避免“露角”或断裂
          const maxJoinLeft = Math.max(0, pointerLeft - (left + r));
          const maxJoinRight = Math.max(0, right - r - pointerRight);
          // 当标注点与气泡某侧边接近共线时，该侧改为直连（无反向圆角）
          const EDGE_ALIGN_EPS = 2;
          const alignedLeft = maxJoinLeft <= EDGE_ALIGN_EPS;
          const alignedRight = maxJoinRight <= EDGE_ALIGN_EPS;
          const jrLeft = alignedLeft
            ? 0
            : Math.max(
                0.2,
                Math.min(joinRadius, pointerHalf - 0.1, maxJoinLeft),
              );
          const jrRight = alignedRight
            ? 0
            : Math.max(
                0.2,
                Math.min(joinRadius, pointerHalf - 0.1, maxJoinRight),
              );
          const isEdgeCorner = edgeCornerLeft || edgeCornerRight;
          // 非左右边缘场景降低尖角圆滑，避免“过圆”
          const effectiveTipRadius = isEdgeCorner
            ? tipRadius
            : Math.min(tipRadius, 0.7);
          const tr = Math.min(
            effectiveTipRadius,
            pointerHalf - 0.1,
            triangleHeight - 0.1,
          );
          // 尖角两侧“肩部”外扩，给圆滑过渡预留更多空间
          const tipShoulderOffset = Math.min(
            pointerHalf - 0.1,
            tr + (isEdgeCorner ? 0.8 : 0.3),
          );
          const tipSign = placeAbove ? 1 : -1;
          const tipRoundY = tipY - tipSign * tr;

          if (placeAbove) {
            if (edgeCornerLeft) {
              // 首点：三角与左短边直连，右角顶点为气泡左下角
              const triBaseX = left + triangleWidth;
              return [
                `M ${left + r} ${top}`,
                `L ${right - r} ${top}`,
                `Q ${right} ${top} ${right} ${top + r}`,
                `L ${right} ${bottom - r}`,
                `Q ${right} ${bottom} ${right - r} ${bottom}`,
                `L ${triBaseX} ${bottom}`,
                `L ${tipShoulderOffset} ${tipRoundY}`,
                `Q 0 ${tipY} ${left} ${tipRoundY}`,
                `L ${left} ${bottom}`,
                `L ${left} ${top + r}`,
                `Q ${left} ${top} ${left + r} ${top}`,
                "Z",
              ].join(" ");
            }
            if (edgeCornerRight) {
              // 末点：三角与右短边直连，直角顶点为气泡右下角
              const triBaseX = right - triangleWidth;
              return [
                `M ${left + r} ${top}`,
                `L ${right - r} ${top}`,
                `Q ${right} ${top} ${right} ${top + r}`,
                `L ${right} ${bottom}`,
                `L ${right} ${tipRoundY}`,
                `Q 0 ${tipY} ${-tipShoulderOffset} ${tipRoundY}`,
                `L ${triBaseX} ${bottom}`,
                `L ${left + r} ${bottom}`,
                `Q ${left} ${bottom} ${left} ${bottom - r}`,
                `L ${left} ${top + r}`,
                `Q ${left} ${top} ${left + r} ${top}`,
                "Z",
              ].join(" ");
            }
            // 气泡在上，指针向下
            return [
              `M ${left + r} ${top}`,
              `L ${right - r} ${top}`,
              `Q ${right} ${top} ${right} ${top + r}`,
              `L ${right} ${bottom - r}`,
              `Q ${right} ${bottom} ${right - r} ${bottom}`,
              `L ${pointerRight + jrRight} ${bottom}`,
              ...(jrRight > 0
                ? [
                    `Q ${pointerRight} ${bottom} ${pointerRight} ${bottom + jrRight}`,
                  ]
                : [`L ${pointerRight} ${bottom}`]),
              `L ${tipShoulderOffset} ${tipRoundY}`,
              `Q 0 ${tipY} ${-tipShoulderOffset} ${tipRoundY}`,
              `L ${pointerLeft} ${bottom + jrLeft}`,
              ...(jrLeft > 0
                ? [
                    `Q ${pointerLeft} ${bottom} ${pointerLeft - jrLeft} ${bottom}`,
                  ]
                : [`L ${pointerLeft} ${bottom}`]),
              `L ${left + r} ${bottom}`,
              `Q ${left} ${bottom} ${left} ${bottom - r}`,
              `L ${left} ${top + r}`,
              `Q ${left} ${top} ${left + r} ${top}`,
              "Z",
            ].join(" ");
          }

          // 气泡在下，指针向上
          if (edgeCornerLeft) {
            // 首点：三角与左短边直连，直角顶点为气泡左上角
            const triBaseX = left + triangleWidth;
            return [
              `M ${left} ${top}`,
              `L ${left} ${tipRoundY}`,
              `Q 0 ${tipY} ${tipShoulderOffset} ${tipRoundY}`,
              `L ${triBaseX} ${top}`,
              `L ${right - r} ${top}`,
              `Q ${right} ${top} ${right} ${top + r}`,
              `L ${right} ${bottom - r}`,
              `Q ${right} ${bottom} ${right - r} ${bottom}`,
              `L ${left + r} ${bottom}`,
              `Q ${left} ${bottom} ${left} ${bottom - r}`,
              `L ${left} ${top}`,
              "Z",
            ].join(" ");
          }
          if (edgeCornerRight) {
            // 末点：三角与右短边直连，直角顶点为气泡右上角
            const triBaseX = right - triangleWidth;
            return [
              `M ${left + r} ${top}`,
              `L ${triBaseX} ${top}`,
              `L ${-tipShoulderOffset} ${tipRoundY}`,
              `Q 0 ${tipY} ${right} ${tipRoundY}`,
              `L ${right} ${top}`,
              `L ${right} ${bottom - r}`,
              `Q ${right} ${bottom} ${right - r} ${bottom}`,
              `L ${left + r} ${bottom}`,
              `Q ${left} ${bottom} ${left} ${bottom - r}`,
              `L ${left} ${top + r}`,
              `Q ${left} ${top} ${left + r} ${top}`,
              "Z",
            ].join(" ");
          }
          return [
            `M ${left + r} ${top}`,
            `L ${pointerLeft - jrLeft} ${top}`,
            ...(jrLeft > 0
              ? [`Q ${pointerLeft} ${top} ${pointerLeft} ${top - jrLeft}`]
              : [`L ${pointerLeft} ${top}`]),
            `L ${-tipShoulderOffset} ${tipRoundY}`,
            `Q 0 ${tipY} ${tipShoulderOffset} ${tipRoundY}`,
            `L ${pointerRight} ${top - jrRight}`,
            ...(jrRight > 0
              ? [`Q ${pointerRight} ${top} ${pointerRight + jrRight} ${top}`]
              : [`L ${pointerRight} ${top}`]),
            `L ${right - r} ${top}`,
            `Q ${right} ${top} ${right} ${top + r}`,
            `L ${right} ${bottom - r}`,
            `Q ${right} ${bottom} ${right - r} ${bottom}`,
            `L ${left + r} ${bottom}`,
            `Q ${left} ${bottom} ${left} ${bottom - r}`,
            `L ${left} ${top + r}`,
            `Q ${left} ${top} ${left + r} ${top}`,
            "Z",
          ].join(" ");
        };

        g.selectAll(".annotation-line").remove();
        g.selectAll(".annotation-bubble-path")
          .data([ann])
          .join("path")
          .attr("class", "annotation-bubble-path")
          .attr("d", buildBubblePath())
          .attr("fill", bubbleBgColor);
        g.selectAll(".annotation-bubble").remove();
        g.selectAll(".annotation-triangle").remove();
        g.selectAll(".annotation-label")
          .data([ann])
          .join("text")
          .attr("class", "annotation-label")
          .attr("x", bubbleCenterX)
          .attr("y", (bubbleTop + bubbleBottom) / 2)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("fill", labelTextColor)
          .attr("font-size", "12px")
          .attr("font-weight", "700")
          .attr("font-family", "Satoshi, sans-serif")
          .text(ann.label);
      }
    } else {
      annGroup.selectAll("*").remove();
    }

    const showTooltip = generateTooltip();
    const xValues = XAxis.map((_d, index) => index);
    const getClosestX = generateGetClosestX({
      svg,
      xScale,
      leftYScale: leftScale,
      xValues,
      lineFlatData,
      lineColorMap,
    });

    addInteractions({
      svg,
      leftMargin: left,
      rightMargin: right,
      topMargin: leftScale(leftTicks.at(-1)!),
      bottomMargin: leftScale(leftTicks[0]),
      getClosestX,
      lineFlatData,
      showTooltip,
    });
    drawEndCallback?.({
      svg,
      xScale,
      yScale: leftScale,
      _preprocessDataResult,
    });
  };
  drawChart();

  // 处理图表自适应尺寸
  const { cleanup: handleChartResizeCleanup } = handleChartResize({
    el: config.el,
    drawChart,
  });

  // 返回清理函数
  return {
    cleanup: () => {
      handleChartResizeCleanup();
    },
    toggleLine,
  };
};
